import type { ProductOfferingCartItems } from '../../../common/types/shoppingBasketCoCo.js';
import type { CustomerCharacteristic } from '../../../generated/api/customerCharacteristic.js';

export const NO_DEVICE_ENROLLMENT_PROGRAM_VALUE = 'NO_DEVICE_ENROLLMENT_PROGRAM';

const CUSTOMER_ENROLLMENTS_CHARACTERISTIC = 'enrollments';
const DEVICE_SUPPORTED_ENROLLMENT_PROGRAMS_SPEC = 'deviceSupportedEnrollmentPrograms_';

export type EnrollmentProgramCustomerIds = {
  program: string;
  customerIds: string[];
};

export enum EnrollmentProgramValue {
  APPLE = 'Apple',
  SAMSUNG = 'Samsung',
  ZERO_TOUCH = 'Zero-Touch',
  AUTOPILOT = 'Autopilot',
}

export enum EnrollmentProgramDisplayLabel {
  APPLE = 'Apple ADE',
  SAMSUNG = 'Samsung KME',
  ZERO_TOUCH = 'Google Zero-Touch',
  AUTOPILOT = 'Windows Autopilot',
}

export const getEnrollmentProgramDisplayLabel = (program: string): string => {
  switch (program) {
    case EnrollmentProgramValue.APPLE:
      return EnrollmentProgramDisplayLabel.APPLE;
    case EnrollmentProgramValue.SAMSUNG:
      return EnrollmentProgramDisplayLabel.SAMSUNG;
    case EnrollmentProgramValue.ZERO_TOUCH:
      return EnrollmentProgramDisplayLabel.ZERO_TOUCH;
    case EnrollmentProgramValue.AUTOPILOT:
    case 'AUTOPILOT':
      return EnrollmentProgramDisplayLabel.AUTOPILOT;
    default:
      return program;
  }
};

/** Shape of each `value` entry of the customer `enrollments` characteristic. */
type CustomerEnrollment = {
  program: string;
  customerId: string;
};

/** Show a customer id dropdown for a program only when `customerIds.length > 1`. */
export const getEnrollmentCustomerIdsByProgram = (
  characteristics: CustomerCharacteristic[] | undefined
): EnrollmentProgramCustomerIds[] => {
  const customerIdsByProgram = new Map<string, Set<string>>();

  (characteristics ?? [])
    .filter(characteristic => characteristic.name === CUSTOMER_ENROLLMENTS_CHARACTERISTIC)
    .flatMap(characteristic => characteristic.value as CustomerEnrollment[])
    .forEach(({ program, customerId }) => {
      if (!program || !customerId) {
        return;
      }
      const customerIds = customerIdsByProgram.get(program) ?? new Set<string>();
      customerIds.add(customerId);
      customerIdsByProgram.set(program, customerIds);
    });

  return Array.from(customerIdsByProgram, ([program, customerIds]) => ({
    program,
    customerIds: Array.from(customerIds),
  }));
};

/** Customer enrollments whose program is supported by at least one device in the cart; `[]` if none. */
export const getMatchingEnrollmentProgramsForCart = (
  cartItems: ProductOfferingCartItems[] | undefined,
  characteristics: CustomerCharacteristic[] | undefined
): EnrollmentProgramCustomerIds[] => {
  const supportedPrograms = new Set(
    (cartItems ?? [])
      .flatMap(item => item.productSpecification ?? [])
      .filter(spec => spec.name === DEVICE_SUPPORTED_ENROLLMENT_PROGRAMS_SPEC)
      .flatMap(spec => spec.value ?? [])
      .map(specValue => specValue.value)
      .filter((value): value is string => !!value)
  );

  return getEnrollmentCustomerIdsByProgram(characteristics).filter(({ program }) => supportedPrograms.has(program));
};
